import React, { FC } from 'react';
import { Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';

import {
  ClientInvitationSearchFilter,
  useGetClientDepartmentsByStatusQuery,
  useGetClientLocationsByStatusQuery,
  ProfileSuggestionFragment,
} from 'api';
import { UserAvatar } from 'components';

interface Props {
  searchFilter: ClientInvitationSearchFilter;
  onSetSearchFilter: (searchFilter: ClientInvitationSearchFilter) => void;
  addedByUsers: ProfileSuggestionFragment[];
}

const useStyles = makeStyles(theme => ({
  container: {
    paddingTop: theme.spacing(2),
    '& > div': {
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  },
}));

const FilterChips: FC<Props> = ({ searchFilter, onSetSearchFilter, addedByUsers }) => {
  const classes = useStyles();

  const { data: selectedDepartments } = useGetClientDepartmentsByStatusQuery();
  const { data: selectedLocations } = useGetClientLocationsByStatusQuery();

  const clientDepartments =
    selectedDepartments?.getClientDepartmentsByStatus?.client_departments ?? [];
  const clientLocations = selectedLocations?.getClientLocationsByStatus?.client_locations ?? [];

  const { department_id__in, location_id__in, added_by_user_id__in } = searchFilter;
  const chipLength =
    department_id__in.length + location_id__in.length + added_by_user_id__in.length;

  function handleRemoveDepartment(id: string) {
    onSetSearchFilter({
      ...searchFilter,
      department_id__in: department_id__in.filter(depId => depId !== id),
    });
  }

  function handleRemoveLocation(id: string) {
    onSetSearchFilter({
      ...searchFilter,
      location_id__in: location_id__in.filter(locId => locId !== id),
    });
  }

  function handleRemoveAddedByUser(id: string) {
    onSetSearchFilter({
      ...searchFilter,
      added_by_user_id__in: added_by_user_id__in.filter(userId => userId !== id),
    });
  }

  function renderDepartmentChips() {
    return department_id__in.map(id => {
      const department = clientDepartments.find(item => item.id === id);
      return (
        <Chip
          key={id}
          variant="outlined"
          label={department.title}
          onDelete={() => handleRemoveDepartment(id)}
          deleteIcon={<CloseIcon fontSize="small" />}
        />
      );
    });
  }

  function renderLocationChips() {
    return location_id__in.map(id => {
      const location = clientLocations.find(item => item.id === id);
      return (
        <Chip
          key={id}
          variant="outlined"
          label={location.title}
          onDelete={() => handleRemoveLocation(id)}
          deleteIcon={<CloseIcon fontSize="small" />}
        />
      );
    });
  }

  function renderAddedByUserChips() {
    return added_by_user_id__in.map(id => {
      const addedByUser = addedByUsers.find(item => item.id === id);
      return (
        <Chip
          key={id}
          variant="outlined"
          label={addedByUser.title}
          onDelete={() => handleRemoveAddedByUser(id)}
          avatar={<UserAvatar user={addedByUser} />}
          deleteIcon={<CloseIcon fontSize="small" />}
        />
      );
    });
  }

  if (!chipLength) {
    return null;
  }

  return (
    <div className={classes.container}>
      {renderDepartmentChips()}
      {renderLocationChips()}
      {renderAddedByUserChips()}
    </div>
  );
};

export default FilterChips;
